import React, { useState, useEffect, useRef } from "react";
import Wordmark from "../../components/Wordmark.jsx";
import EditClientModal from "../../components/coordinator/EditClientModal.jsx";
import NewClientModal from "../../components/coordinator/NewClientModal.jsx";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import UserChip from "../../components/UserChip.jsx";
import ClientNameBlock from "../../components/ClientNameBlock.jsx";
import AccessPanel from "../../components/AccessPanel.jsx";

function Topbar({ user, onLogout }) {
  const initials = user?.initials || "?";
  return (
    <div style={tb.bar}>
      <Wordmark />
      <div style={tb.right}>
        <UserChip user={user} onLogout={onLogout} />
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const colors = { live: "var(--color-status-live)", draft: "var(--color-status-draft)", empty: "var(--color-text-tertiary)" };
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors[status] || colors.empty, display: "inline-block", flexShrink: 0 }} />;
}

function timeAgo(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)   return mins <= 1 ? "Edited just now" : `Edited ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `Edited ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1)  return "Edited yesterday";
  if (days < 7)    return `Edited ${days} days ago`;
  return `Edited ${Math.floor(days / 7)}w ago`;
}


const modal = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  box:       { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-8)", width: 480, maxWidth: "calc(100vw - 48px)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  close:     { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  label:     { display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  req:       { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-critical)", display: "inline-block", marginLeft: "var(--space-2)" },
  input:     { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  row:       { display: "flex", gap: "var(--space-3)" },
  footer:    { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  error:     { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  cancelBtn: { fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", color: "var(--color-text-secondary)", cursor: "pointer" },
  createBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
};

// ── Delete confirm dialog ──────────────────────────────────────────────────

function DeleteDialog({ client, onClose, onDeleted }) {
  const [input,   setInput]   = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error,   setError]   = useState(null);
  const confirmed = input.trim().toLowerCase() === client.display_name.toLowerCase();

  async function handleDelete() {
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`/api/clients/${client.id}`, { method: "DELETE" });
      if (!res.ok) { const d = await res.json(); setError(d.error || "Delete failed"); setDeleting(false); return; }
      onDeleted(client.id);
    } catch (e) { setError("Network error."); setDeleting(false); }
  }

  return (
    <div style={modal.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal.box}>
        <div style={modal.header}>
          <h2 style={{ ...modal.title, color: "var(--color-critical)" }}>Delete {client.display_name}?</h2>
          <button style={modal.close} onClick={onClose}>×</button>
        </div>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)" }}>
          This permanently removes the client and all their content — sections, entities, documents, media, and stream records. This cannot be undone.
        </p>
        <label style={modal.label}>
          Type <strong>{client.display_name}</strong> to confirm
          <input style={{ ...modal.input, borderColor: confirmed ? "var(--color-critical)" : "var(--color-border)" }}
            value={input} onChange={e => setInput(e.target.value)}
            placeholder={client.display_name}
            onKeyDown={e => e.key === "Enter" && confirmed && handleDelete()} />
        </label>
        {error && <p style={modal.error}>{error}</p>}
        <div style={modal.footer}>
          <button style={modal.cancelBtn} onClick={onClose} disabled={deleting}>Cancel</button>
          <button style={{ ...modal.deleteBtn, ...(!confirmed || deleting ? modal.btnDisabled : {}) }}
            onClick={handleDelete} disabled={!confirmed || deleting}>
            {deleting ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

export default function CoordinatorClients() {
  const { user, logout, can } = useAuth();
  // Adding clients can be admin-only, by agency setting (Oct 19).
  const canCreate = can("create_client");
  const navigate          = useNavigate();
  const [clients,    setClients]    = useState(null);
  const [error,      setError]      = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);   // client obj to delete

  function load() {
    fetch("/api/coordinator/clients")
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(setClients)
      .catch(e => setError(e.message));
  }

  useEffect(() => { load(); }, []);

  // New notes from the team, per client (Getting Good, Nov 2026): one request
  // for the whole list. A badge that fails to load is just absent.
  const [noticed, setNoticed] = useState({});
  useEffect(() => {
    fetch("/api/me/notes/inbox-counts")
      .then(r => (r.ok ? r.json() : {}))
      .then(d => setNoticed(d || {}))
      .catch(() => {});
  }, []);

  async function handleLogout() { await logout(); navigate("/login", { replace: true }); }

  function handleCreated(clientId) {
    setShowCreate(false);
    navigate(`/coordinator/clients/${clientId}/quick-start`);
  }

  function handleDeleted(clientId) {
    setDeleteTarget(null);
    setClients(prev => prev ? prev.filter(c => c.id !== clientId) : prev);
  }

  // null means still loading; an empty array means no profiles yet. Keeping
  // the two distinct is the whole point: loading must not flash the empty
  // state, and the empty case must not show a count of zero.
  const isEmpty = clients !== null && clients.length === 0;

  return (
    <div style={styles.page}>
      <Topbar user={user} onLogout={handleLogout} />

      {editTarget && (
        <EditClientModal client={editTarget} onClose={() => setEditTarget(null)}
                         onSaved={d => setClients(prev => prev.map(c => c.id === d.id ? { ...c, ...d } : c))} />
      )}
      {showCreate && <NewClientModal onClose={() => setShowCreate(false)} onCreate={handleCreated} />}
      {deleteTarget && <DeleteDialog client={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={handleDeleted} />}

      <div style={styles.container}>
        {/* "Your clients / 0 clients" above an empty grid is the wrong first
            screen for a coordinator who has just signed up. With nothing yet,
            the header gives way to a single invitation to start. */}
        {!isEmpty && (
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.heading}>Your clients</h1>
              {clients && <p style={styles.subtitle}>{clients.length} client{clients.length !== 1 ? "s" : ""}</p>}
            </div>
            {canCreate && <button style={styles.newBtn} onClick={() => setShowCreate(true)}>+ New client</button>}
          </div>
        )}

        {error && <p style={{ color: "var(--color-critical)", fontSize: "var(--text-sm)" }}>{error}</p>}
        {clients === null && !error && <p style={{ color: "var(--color-text-tertiary)", fontSize: "var(--text-sm)" }}>Loading…</p>}

        {isEmpty && (
          <div style={styles.empty}>
            <h1 style={styles.heading}>No profiles yet</h1>
            {canCreate ? (<>
              <p style={styles.emptyBody}>
                Start with someone you support. You can add what you know now and
                build it up.
              </p>
              <button style={styles.emptyBtn} onClick={() => setShowCreate(true)}>
                Add a person
              </button>
            </>) : (
              <p style={styles.emptyBody}>
                Your agency's admin adds new profiles. When one is shared with
                you, it'll appear here.
              </p>
            )}
          </div>
        )}

        <div style={styles.grid}>
          {(clients || []).map(client => {
            const { live = 0, draft = 0, empty = 0 } = client.section_counts || {};
            const meta = [client.age && `${client.age}`, client.location].filter(Boolean).join(" · ");
            return (
              <div key={client.id} style={styles.cardWrap}>
                <button type="button"
                  style={styles.deleteBtn}
                  onClick={() => setDeleteTarget(client)}
                  aria-label={`Delete ${client.display_name}`}
                  title={`Delete ${client.display_name}`}
                >
                  ×
                </button>
                <Link to={`/coordinator/clients/${client.id}/edit`} style={styles.card}>
                  <div style={styles.cardHead}>
                    <div style={{ ...styles.avatar, background: "var(--color-accent-subtle)", color: "var(--color-accent)" }}>
                      {client.display_name[0]}
                    </div>
                    <div>
                      <ClientNameBlock displayName={client.display_name} fullName={client.full_name} nameStyle={styles.clientName}
                                       editable onEdit={() => setEditTarget(client)} />
                      {meta && <div style={styles.clientMeta}>{meta}</div>}
                    </div>
                  </div>
                  <div style={styles.statusRow}>
                    {live > 0  && <span style={styles.statusPill}><StatusDot status="live"  />{live} live</span>}
                    {draft > 0 && <span style={styles.statusPill}><StatusDot status="draft" />{draft} draft</span>}
                    {empty > 0 && <span style={styles.statusPill}><StatusDot status="empty" />{empty} empty</span>}
                    {noticed[client.id] > 0 && (
                      <span style={{ ...styles.statusPill, ...styles.noticedPill }}
                            title="Shared by the team, not yet reviewed">{noticed[client.id]} noticed</span>
                    )}
                    <span style={styles.lastEdit}>{timeAgo(client.last_edited)}</span>
                  </div>
                </Link>
                {/* The people who know this client best usually don't work here.
                    This is how a coordinator asks them to help. Two actions,
                    one style: quiet outline chips at the row's edge (C8). */}
                <button type="button" style={styles.rowChip} onClick={() => setAccessTarget(client)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8v6M22 11h-6"/></svg>
                  Invite to view or add to profile
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {accessTarget && (
        <AccessPanel client={accessTarget} onClose={() => setAccessTarget(null)} />
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const tb = {
  bar:       { background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  wordmark:  { fontWeight: "var(--weight-medium)", fontSize: 17, letterSpacing: "-0.3px", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 6 },
  dot:       { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--color-accent)" },
  right:     { display: "flex", alignItems: "center", gap: 16 },
  avatar:    { width: 30, height: 30, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 13 },
};

const styles = {
  // Full width of the card above it, so the pair reads as one unit.
  rowChip:     { font: "inherit", fontSize: "var(--text-sm)", height: 36, padding: "0 12px", display: "flex", width: "100%", boxSizing: "border-box", justifyContent: "center", marginTop: 8, alignItems: "center", gap: 6, border: "1px solid var(--color-border-strong)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", color: "var(--color-text-secondary)", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 },
  page:       { minHeight: "100dvh", background: "var(--color-bg-subtle)" },
  container:  { maxWidth: 960, margin: "0 auto", padding: "40px 32px 80px" },
  headerRow:  { display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 },
  heading:    { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", letterSpacing: "-0.5px", color: "var(--color-text-primary)" },
  subtitle:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginTop: 4 },
  newBtn:     { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  empty:      { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-xl)", padding: "48px 40px", textAlign: "center", maxWidth: 540, margin: "24px auto" },
  emptyBody:  { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: "14px auto 28px", maxWidth: 420 },
  // `font: inherit` goes FIRST so the explicit size and weight below win. The
  // equivalent button in FamilyClients puts it last, where the shorthand
  // silently overrides both — worth fixing there in a later pass.
  emptyBtn:   { font: "inherit", background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "12px 22px", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer" },
  grid:       { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  cardWrap:   { position: "relative" },
  card:       { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "22px 24px", textDecoration: "none", color: "var(--color-text-primary)", display: "block" },
  cardHead:   { display: "flex", alignItems: "center", gap: 14, marginBottom: 16 },
  avatar:     { width: 48, height: 48, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 17, flexShrink: 0 },
  clientName: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" },
  clientMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 2 },
  statusRow:  { display: "flex", alignItems: "center", gap: 12, paddingTop: 14, borderTop: "1px solid var(--color-border)", fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", flexWrap: "wrap" },
  statusPill: { display: "inline-flex", alignItems: "center", gap: 5 },
  noticedPill: { padding: "1px 8px", borderRadius: 999, background: "var(--color-accent-subtle)", color: "var(--color-accent)", fontWeight: "var(--weight-medium)" },
  lastEdit:   { marginLeft: "auto", fontSize: 11, color: "var(--color-text-tertiary)" },
  deleteBtn:  { position: "absolute", top: 10, right: 10, width: 24, height: 24, borderRadius: "var(--radius-full)", background: "var(--color-bg-inset)", border: "1px solid var(--color-border)", color: "var(--color-text-tertiary)", fontSize: 16, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
};
